// ---------------------------------------------------------------------------
// HubSpot, the CRM connections and the Lead Status mapping (R18b, from pages/settings.ts)
//
//   - Connecting HubSpot is a popup too, but the answer comes back through
//     localStorage: the server's /auth/hubspot/callback page writes
//     'hubspot_oauth_callback' (entrypoints/sdr/src/server/startup.ts), which
//     is read every 800 ms, then the code is exchanged for the connection.
//     The popup opens INSIDE the click, before the request for the sign-in
//     URL (see settings-accounts.ts). The flow ends when the popup closes or
//     after five minutes, counted from the clock rather than by a timer.
//   - Disconnecting a CRM asks first; existing mappings stay.
//   - The Lead Status panel of a HubSpot connection reads the portal's
//     options and one row per state of ours; its draft starts from the saved
//     value, else the best guess; "Suggest" replaces the draft with the
//     suggestion; "Save" stores it and reads the panel again.
//
// The CRM calls themselves are crm-api.ts's (REST, R11b); the page hands them in.
// ---------------------------------------------------------------------------

import { confirmDialog } from '../confirm-dialog.js';
import type { HubspotLeadStatusMappingView } from '../crm-api.js';
import type { AuthPopup } from '../oauth-popup.js';
import { browserTimers, type Say, type Send, type Timers, viaFetch } from './settings-flow.js';

/** The localStorage key the server's callback page writes (startup.ts); client and server must agree on it. */
export const HUBSPOT_CALLBACK_KEY = 'hubspot_oauth_callback';
export const HUBSPOT_POLL_MS = 800;
export const HUBSPOT_GIVE_UP_MS = 5 * 60 * 1000;

/** Where HubSpot sends the person back to: the server's callback page. */
export const hubspotRedirectUri = (origin: string) => `${origin}/auth/hubspot/callback`;

export interface HubspotDeps {
  send?: Send;
  say: Say;
  openPopup(opts: { message?: string; width?: number; height?: number }): AuthPopup;
  timers?: Timers;
  storage: Pick<Storage, 'getItem' | 'removeItem'>;
  /** window.location.origin */
  origin: string;
  setConnecting(busy: boolean): void;
  clearMessages(): void;
  reload(): Promise<void>;
}

/** Connects a HubSpot portal. Opens the popup before it returns: call it straight from the click. */
export function connectHubspot(deps: HubspotDeps): Promise<void> {
  deps.clearMessages();
  deps.setConnecting(true);
  // Opened before the first await — see settings-accounts.ts.
  const popup = deps.openPopup({ message: 'Connecting HubSpot…', width: 720, height: 820 });
  return pollHubspot(popup, deps);
}

async function pollHubspot(popup: AuthPopup, deps: HubspotDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  const timers = deps.timers ?? browserTimers;
  try {
    const redirectUri = hubspotRedirectUri(deps.origin);
    const initRes = await send(`/api/crm/hubspot/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`);
    const initData = await initRes.json();
    if (!initData.ok) {
      popup.close();
      deps.say.error(initData.error ?? 'Failed to start HubSpot connection');
      deps.setConnecting(false);
      return;
    }

    // Clear any stale callback before handing the popup to HubSpot
    deps.storage.removeItem(HUBSPOT_CALLBACK_KEY);

    if (!(await popup.navigate(initData.url))) {
      deps.setConnecting(false);
      return;
    }

    const startedAt = timers.now();

    const poll = timers.setInterval(async () => {
      try {
        const raw = deps.storage.getItem(HUBSPOT_CALLBACK_KEY);
        if (raw) {
          timers.clearInterval(poll);
          popup.close();
          deps.storage.removeItem(HUBSPOT_CALLBACK_KEY);
          const parsed = JSON.parse(raw) as { code?: string; error?: string; errorDescription?: string };
          if (parsed.error || !parsed.code) {
            deps.say.error(parsed.errorDescription ?? parsed.error ?? 'HubSpot authorization was cancelled');
            deps.setConnecting(false);
            return;
          }
          const cbRes = await send('/api/crm/hubspot/callback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: parsed.code, redirectUri }),
          });
          const cbData = await cbRes.json();
          if (cbData.ok) {
            deps.say.success(`HubSpot connected (${cbData.accountLabel ?? cbData.accountId ?? ''})`);
            void deps.reload();
          } else {
            deps.say.error(cbData.error ?? 'HubSpot connection failed');
          }
          deps.setConnecting(false);
          return;
        }
        if (popup.isClosed() || timers.now() - startedAt > HUBSPOT_GIVE_UP_MS) {
          timers.clearInterval(poll);
          deps.setConnecting(false);
        }
      } catch {
        /* keep polling */
      }
    }, HUBSPOT_POLL_MS);
  } catch (err) {
    popup.close();
    deps.say.error(err instanceof Error ? err.message : 'HubSpot connection failed');
    deps.setConnecting(false);
  }
}

export interface DisconnectCrmDeps {
  say: Say;
  /** crm-api.ts: false when the server knows no connection of yours with this id. */
  disconnectCrmConnection(connectionId: string): Promise<boolean>;
  confirm?: typeof confirmDialog;
  reload(): Promise<void>;
}

/** Asks, then disconnects a CRM connection. */
export async function disconnectCrm(connectionId: string, label: string, deps: DisconnectCrmDeps): Promise<void> {
  const confirm = deps.confirm ?? confirmDialog;
  if (
    !(await confirm(
      `Disconnect ${label}? Existing CRM mappings remain in place but no further updates will be pushed.`,
      { title: 'Disconnect CRM', confirmLabel: 'Disconnect' },
    ))
  )
    return;
  try {
    const ok = await deps.disconnectCrmConnection(connectionId);
    if (ok) {
      deps.say.success('CRM disconnected.');
      void deps.reload();
    } else {
      deps.say.error('Connection not found.');
    }
  } catch (err) {
    deps.say.error(err instanceof Error ? err.message : 'Failed to disconnect CRM');
  }
}

// --- The Lead Status mapping ------------------------------------------------

/** localStatus → the chosen HubSpot option ('' = leave unset). */
export type MappingDraft = Record<string, string>;

/** The draft a panel opens with: the saved value, else the best guess, else unset. */
export function draftFromSaved(view: HubspotLeadStatusMappingView): MappingDraft {
  const draft: MappingDraft = {};
  for (const r of view.rows ?? []) draft[r.localStatus] = r.savedValue || r.suggestedValue || '';
  return draft;
}

/** The draft after "Suggest": the suggestion, else unset. */
export function draftFromSuggestion(view: HubspotLeadStatusMappingView): MappingDraft {
  const draft: MappingDraft = {};
  for (const r of view.rows ?? []) draft[r.localStatus] = r.suggestedValue || '';
  return draft;
}

/** What the panel is doing. */
export type MappingBusy = '' | 'load' | 'suggest' | 'save';

/** The mapping calls of crm-api.ts. */
export interface MappingApi {
  getHubspotLeadStatusMapping(connectionId: string): Promise<HubspotLeadStatusMappingView>;
  suggestHubspotLeadStatusMapping(connectionId: string): Promise<HubspotLeadStatusMappingView>;
  saveHubspotLeadStatusMapping(connectionId: string, mapping: MappingDraft): Promise<void>;
}

export interface MappingDeps extends MappingApi {
  say: Say;
  setOpen(connectionId: string | null): void;
  setView(view: HubspotLeadStatusMappingView | null): void;
  setDraft(draft: MappingDraft): void;
  setBusy(busy: MappingBusy): void;
  setError(error: string | null): void;
}

const reason = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

/** Opens a connection's panel and reads it, or closes it when it is the open one. */
export async function toggleMapping(connectionId: string, openId: string | null, deps: MappingDeps): Promise<void> {
  if (openId === connectionId) {
    deps.setOpen(null);
    deps.setView(null);
    return;
  }
  deps.setOpen(connectionId);
  deps.setView(null);
  deps.setError(null);
  deps.setBusy('load');
  try {
    const view = await deps.getHubspotLeadStatusMapping(connectionId);
    deps.setView(view);
    deps.setDraft(draftFromSaved(view));
  } catch (err) {
    deps.setError(reason(err, 'Failed to load the mapping'));
  } finally {
    deps.setBusy('');
  }
}

/** Replaces the draft with the server's suggestion. */
export async function suggestMapping(openId: string | null, deps: MappingDeps): Promise<void> {
  if (!openId) return;
  deps.setBusy('suggest');
  deps.setError(null);
  try {
    const view = await deps.suggestHubspotLeadStatusMapping(openId);
    deps.setView(view);
    deps.setDraft(draftFromSuggestion(view));
    deps.say.success(
      view.aiSuggested ? 'AI suggestion applied — review and save.' : 'Suggestion applied — review and save.',
    );
  } catch (err) {
    deps.setError(reason(err, 'Failed to suggest a mapping'));
  } finally {
    deps.setBusy('');
  }
}

/** Stores the draft and reads the panel again. */
export async function saveMapping(openId: string | null, draft: MappingDraft, deps: MappingDeps): Promise<void> {
  if (!openId) return;
  deps.setBusy('save');
  deps.setError(null);
  try {
    await deps.saveHubspotLeadStatusMapping(openId, draft);
    deps.say.success('Lead Status mapping saved.');
    const view = await deps.getHubspotLeadStatusMapping(openId);
    deps.setView(view);
    deps.setDraft(draftFromSaved(view));
  } catch (err) {
    deps.setError(reason(err, 'Failed to save the mapping'));
  } finally {
    deps.setBusy('');
  }
}
